import { FC, FormEvent, useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";

import AthleteProfileEditor from "./AthleteProfileEditor";
import { DOBSelect, adultBirthYears } from "../Dashboard/Athletes/Options";
import FormDropdown from "../Dashboard/Dropdown";
import { User } from "../../models/User";
import { authClasses } from "../../pages/Auth/authClasses";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";

interface ProfileCardProps {
  handleLogout: () => Promise<void>;
  user: User;
}

const ProfileCard: FC<ProfileCardProps> = (props) => {
	const {
		handleLogout,
		user,
		user: { email },
	} = props;
	const { buttonSmall, input } = authClasses;
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [phone, setPhone] = useState("");
	const [gender, setGender] = useState("Gender");
	const [month, setMonth] = useState("Month");
	const [day, setDay] = useState("Day");
	const [year, setYear] = useState("Year");

	const getDateFromUserData = (value: any) => {
		if (!value) {
			return null;
		}
		if (value instanceof Date) {
			return value;
		}
		if (typeof value.toDate === "function") {
			return value.toDate();
		}
		return null;
	};

	const monthNames = [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December",
	];

	const buildDOB = () => {
		const monthIndex = monthNames.indexOf(month);
		const parsedDay = Number(day);
		const parsedYear = Number(year);

		if (
			monthIndex < 0 ||
			!Number.isInteger(parsedDay) ||
			!Number.isInteger(parsedYear)
		) {
			return null;
		}

		const dob = new Date(parsedYear, monthIndex, parsedDay);
		if (
			dob.getFullYear() !== parsedYear ||
			dob.getMonth() !== monthIndex ||
			dob.getDate() !== parsedDay
		) {
			return null;
		}

		return dob;
	};

	useEffect(() => {
		const fetchProfile = async () => {
			setLoading(true);
			try {
				const snap = await getDoc(doc(db, "users", user.id));
				const data = snap.data();
				const dob = getDateFromUserData(data?.dob);

				setFirstName(data?.firstName ?? "");
				setLastName(data?.lastName ?? "");
				setPhone(data?.phone ?? "");
				setGender(data?.gender ?? "Gender");
				setMonth(
					dob ? dob.toLocaleString("default", { month: "long" }) : "Month",
				);
				setDay(dob ? String(dob.getDate()) : "Day");
				setYear(dob ? String(dob.getFullYear()) : "Year");
			} finally {
				setLoading(false);
			}
		};

		fetchProfile();
	}, [user.id]);

	const handleSaveProfile = async (event: FormEvent) => {
		event.preventDefault();
		setMessage("");

		const dob = buildDOB();
		const normalizedPhone = phone.replace(/\D/g, "").slice(0, 10);
		if (
			firstName.trim() === "" ||
			lastName.trim() === "" ||
			normalizedPhone.length !== 10 ||
			gender === "Gender" ||
			dob === null
		) {
			setMessage("Complete first name, last name, phone, gender, and DOB.");
			return;
		}

		setSaving(true);
		try {
			await updateDoc(doc(db, "users", user.id), {
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				phone: normalizedPhone,
				gender,
				dob,
			});
			setPhone(normalizedPhone);
			setMessage("Profile saved.");
			navigate("/");
		} catch (error) {
			setMessage("Could not save profile. Try again or contact Sidewinder.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="min-h-[calc(100vh-64px)] bg-[#1c1d21] px-4 py-6 text-slate-100 md:px-8">
			<div className="mx-auto grid w-full max-w-4xl gap-5 rounded-lg border border-white/10 bg-[#24262c] p-5 shadow-2xl shadow-black/30">
				<div className="flex flex-col items-center border-b border-white/10 pb-5">
					<div className="mb-3 grid h-24 w-24 place-content-center rounded-full bg-accent-color-2 text-4xl font-bold text-black shadow-lg">
						{email[0].toUpperCase()}
					</div>
					<span className="text-sm text-slate-300">{email}</span>
					<div className="mt-4 flex flex-wrap justify-center gap-3">
						<button
							onClick={handleLogout}
							className={`${buttonSmall} border-white/10 bg-[#333333] hover:bg-[#3f3f3f]`}
						>
							Logout
						</button>
					</div>
				</div>

				<form className="grid gap-4" onSubmit={handleSaveProfile}>
					<div>
						<h2 className="m-0 text-xl text-white">Your Information</h2>
						<p className="m-0 text-sm text-slate-400">
							This is your profile information for league registration.
						</p>
					</div>
					<div className="grid gap-3 md:grid-cols-2">
						<input
							className={input}
							disabled={loading}
							placeholder="First name"
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
						/>
						<input
							className={input}
							disabled={loading}
							placeholder="Last name"
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
						/>
						<input
							className={input}
							disabled={loading}
							inputMode="tel"
							placeholder="Phone number"
							value={phone}
							onChange={(e) =>
								setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
							}
						/>
						<FormDropdown
							options={["Gender", "Female", "Male", "Non-binary"]}
							value={gender}
							setVal={setGender}
						/>
					</div>
					<div className="grid gap-1.5">
						<small className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
							Date of Birth
						</small>
						<DOBSelect
							day={day}
							setDay={setDay}
							month={month}
							setMonth={setMonth}
							year={year}
							setYear={setYear}
							yearOptions={adultBirthYears}
						/>
					</div>
					<div className="flex flex-wrap items-center justify-end gap-3 border-t border-white/10 pt-4">
						{message ? <small className="text-slate-300">{message}</small> : null}
						<button className={buttonSmall} disabled={loading || saving}>
							{saving ? "Saving..." : "Save Profile"}
						</button>
					</div>
				</form>
				<AthleteProfileEditor user={user} />
			</div>
		</div>
	);
};

export default ProfileCard;
